/*
 * Copyright (c) 2023, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: MIT
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/MIT
 */

import {
    getOwnPropertyDescriptor,
    getOwnPropertyDescriptors,
    MapCreate,
} from '../shared/utils';

const lengthGetterOriginal = getOwnPropertyDescriptor(window, 'length')!.get as (this: Window & typeof globalThis) => any;

function lengthGetterDistortion() {
    return window.length;
};

function isWindow(value: any): boolean {
    if (
        typeof value === 'object' &&
        value !== null &&
        value.window === value
    ) {
        // Slower check that must certainly detect a window object.
        try {
            // window.self getter only works for a window object, otherwise it
            // throws, additionally, this works fine for a detached window as
            // well, which is important since it will guarantee that this check
            // works also for iframes that are disconnected, and could be connected
            // later on, that should not bypass this check. This check is is also
            // equivalent to window.frames, and here is the very simple spec for
            // this getter:
            // https://html.spec.whatwg.org/multipage/window-object.html#dom-self
            Reflect.apply(lengthGetterOriginal, value, []);
            return true;
            // eslint-disable-next-line no-empty
        } catch {}
    }
    return false;
}

type FrameListProxy = {
    [K in string]: Window;
};

// The rules for this proxy are as follows:
//
// If the key is a string...
//      ...And the key is a valid index that matches an existing
//      index in the frame list or matches the value of a name
//      property of a frame in the frame list, then return the
//      appropriate frame from the frame list.
//
//      ...Or the key's value is "length", then return the number of
//      frames in the in the frame list.
//  Else,
//      ...Return the value of the key from the shadow target
class BaseFrameHandler implements ProxyHandler<object> {
    defineProperty(
        _target: object,
        _key: PropertyKey,
        _descriptor: PropertyDescriptor
    ): ReturnType<typeof Reflect.defineProperty> {
        return true;
    }

    deleteProperty(_target: object, _key: PropertyKey): ReturnType<typeof Reflect.deleteProperty> {
        return true;
    }

    getOwnPropertyDescriptor(
        _target: object,
        _key: PropertyKey
    ): ReturnType<typeof Reflect.getOwnPropertyDescriptor> {
        return undefined;
    }

    isExtensible(_target: object): ReturnType<typeof Reflect.isExtensible> {
        return true;
    }

    ownKeys(_target: object): ReturnType<typeof Reflect.ownKeys> {
        return [];
    }

    preventExtensions(_target: object): ReturnType<typeof Reflect.preventExtensions> {
        return true;
    }

    set(
        _target: object,
        _key: PropertyKey,
        _value: any,
        _receiver: any
    ): ReturnType<typeof Reflect.set> {
        return true;
    }

    setPrototypeOf(_target: object): ReturnType<typeof Reflect.setPrototypeOf> {
        return false;
    }
}

Reflect.setPrototypeOf(BaseFrameHandler.prototype, null);

class ShadowFramesPrototypeHandler extends BaseFrameHandler {}

const framesGetterOriginal = getOwnPropertyDescriptor(window, 'frames')!.get;

const createFramesProxy = () => {
    const WindowPrototype = Reflect.getPrototypeOf(window)!;
    const WindowProperties = Reflect.getPrototypeOf(WindowPrototype)!;
    // Wrap `lengthGetterOriginal` in bound function to obscure the getter
    // source as "[native code]".
    const framesLengthGetter = lengthGetterOriginal?.bind(window);
    // Wrap `noop` in bound function to obscure the setter source as
    // "[native code]".
    const framesLengthSetter = (function length() {}).bind(window);

    const getFrameByIndexKey = (key: PropertyKey): Window | undefined => {
        const possibleIndex = typeof key === 'string' ? +key : -1;
        if (
            possibleIndex > -1 &&
            Number.isInteger(possibleIndex) &&
            possibleIndex < Reflect.apply(lengthGetterOriginal, window, [])
        ) {
            const value = window.hasOwnProperty(key) && window[key];
            // istanbul ignore else: needs default platform behavior test
            if (isWindow(value)) {
                return value as unknown as Window;
            }
        }
        return undefined;
    };

    const getFrameByNameKey = (key: PropertyKey): Window | undefined => {
        if (
            typeof key === 'string' &&
            // Don't shadow properties on the global object...
            !window.hasOwnProperty(key) &&
            // ...Or its prototype.
            !WindowPrototype.hasOwnProperty(key)
        ) {
            const value = WindowProperties.hasOwnProperty(key) && WindowProperties[key];
            if (isWindow(value)) {
                return value as unknown as Window;
            }
        }
        return undefined;
    };

    const getValueByKey = (key: PropertyKey): Window | number | undefined =>
        key === 'length'
            ? Reflect.apply(lengthGetterOriginal, window, [])
            : getFrameByIndexKey(key);

    class ShadowFrameHandler extends BaseFrameHandler {
        get(target: object, key: PropertyKey, receiver: any): ReturnType<typeof Reflect.get> {
            const value = getValueByKey(key);
            return value === undefined
                ? // window.frames.foo when iframe.name is 'foo'
                    Reflect.get(target, key, receiver)
                : // window.frames.length
                    // window.frames[n]
                    // window.frames['n']
                    value;
        }

        getOwnPropertyDescriptor(
            _target: object,
            key: PropertyKey
        ): ReturnType<typeof Reflect.getOwnPropertyDescriptor> {
            if (key === 'length') {
                return {
                    __proto__: null,
                    configurable: true,
                    enumerable: true,
                    get: framesLengthGetter,
                    set: framesLengthSetter,
                } as PropertyDescriptor;
            }
            const value = getFrameByIndexKey(key);
            if (value) {
                return {
                    __proto__: null,
                    configurable: true,
                    enumerable: true,
                    value,
                    writable: false,
                } as PropertyDescriptor;
            }
            return undefined;
        }

        // istanbul ignore next: suspicious gap, currently unreachable via tests
        has(target: object, key: PropertyKey): ReturnType<typeof Reflect.has> {
            return Reflect.has(target, key) || getValueByKey(key) !== undefined;
        }

        ownKeys(): ReturnType<typeof Reflect.ownKeys> {
            const { length } = window;
            const keys = new Array(length + 1);
            for (let i = 0; i < length; i += 1) {
                keys[i] = `${i}`;
            }
            keys[length] = 'length';
            return keys;
        }
    }

    class ShadowWindowPropertiesHandler extends ShadowFramesPrototypeHandler {
        get(target: object, key: PropertyKey, receiver: any): ReturnType<typeof Reflect.get> {
            const value = getFrameByNameKey(key);
            return value === undefined
                ? // window.frames.foo when iframe.name is 'foo'
                    Reflect.get(target, key, receiver)
                : /* istanbul ignore next: needs default platform behavior test */ value;
        }

        getOwnPropertyDescriptor(
            _target: object,
            key: PropertyKey
        ): ReturnType<typeof Reflect.getOwnPropertyDescriptor> {
            const value = getFrameByNameKey(key);
            if (value === undefined) {
                return value;
            }
            return {
                __proto__: null,
                configurable: true,
                enumerable: true,
                value,
                writable: false,
            } as PropertyDescriptor;
        }

        // istanbul ignore next: suspicious gap, currently unreachable via tests
        has(target: object, key: PropertyKey): ReturnType<typeof Reflect.has> {
            return Reflect.has(target, key) || getFrameByNameKey(key) !== undefined;
        }

        ownKeys(): ReturnType<typeof Reflect.ownKeys> {
            const keys: string[] = [];
            let keysOffset = 0;
            const unsafeDescs = getOwnPropertyDescriptors(WindowProperties);
            Reflect.setPrototypeOf(unsafeDescs, null);
            for (const key in unsafeDescs) {
                // istanbul ignore next: currently unreachable via tests, may indicate a dead code path
                if (typeof key === 'string') {
                    const unsafeDesc = unsafeDescs[key];
                    if (unsafeDesc.hasOwnProperty('value') && isWindow(unsafeDesc.value)) {
                        keys[keysOffset++] = key;
                    }
                }
            }
            return keys;
        }
    }
    // In order to preserve window.frames === window.frames, create a
    // shadow target object, to be used with the proxy object that is
    // returned by accesses to window.frames.
    const shadowFrames = {};
    const shadowFramesPrototype = {};
    const shadowWindowProperties = {};

    const shadowFramesHandler = new ShadowFrameHandler();
    const shadowFramesPrototypeHandler = new ShadowFramesPrototypeHandler();
    const shadowWindowPropertiesHandler = new ShadowWindowPropertiesHandler();

    const framesPrototypeProxy = new Proxy(
        shadowFramesPrototype,
        shadowFramesPrototypeHandler
    ) as FrameListProxy;

    const windowPropertiesProxy = new Proxy(
        shadowWindowProperties,
        shadowWindowPropertiesHandler
    ) as FrameListProxy;

    Reflect.setPrototypeOf(shadowFrames, framesPrototypeProxy);
    Reflect.setPrototypeOf(shadowFramesPrototype, windowPropertiesProxy);
    return new Proxy(shadowFrames, shadowFramesHandler) as FrameListProxy;
};

let framesProxy: FrameListProxy;

const framesGetterDistortion = function frames(): FrameListProxy {
    if (framesProxy === undefined) {
        framesProxy = createFramesProxy();
    }
    return framesProxy;
}

export default MapCreate([
    [lengthGetterOriginal, lengthGetterDistortion],
    [framesGetterOriginal, framesGetterDistortion],
]);